import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { bookingApi } from '../../../utils/bookingApi';
import EmbeddedStripe from '../../../components/EmbeddedStripe';
import { formatMonth } from '../../../utils/dates';
import { PageHeader, Skeleton, ErrorState } from '../../../components/ui';
import '../booking-shared.css';
import './adminTables.css';

const STATUS_TEXT = {
  PENDING: 'Stripe is checking your details',
  ACTIVE: 'Card payments are on',
  RESTRICTED: 'Stripe needs more information',
  CLOSED: 'Stripe is disconnected. Ask Run The Club to reopen card payments.',
};

// What happened when the admin came back from connecting an existing Stripe account
const OUTCOMES = {
  connected: { ok: true, text: 'Your Stripe account is connected.' },
  already_connected: { text: 'Your club already takes card payments through another Stripe account. Ask Run The Club if you need to change it.' },
  in_use: { text: 'That Stripe account is already connected to another club.' },
  expired: { text: 'That link expired. Try again.' },
  cancelled: { text: "You didn't finish connecting Stripe. Nothing has changed." },
  error: { text: 'Something went wrong connecting Stripe. Try again, or contact Run The Club.' },
};

const METHOD_TEXT = { bacs_debit: 'Direct Debit', card: 'card' };
const BILL_STATUS = {
  OPEN: { label: 'Due', tone: 'wait' },
  PAID: { label: 'Paid', tone: 'good' },
  VOID: { label: 'Cancelled', tone: 'off' },
  UNCOLLECTIBLE: { label: 'Not collected', tone: 'bad' },
};
const ADMIN = { to: '/admin-hub', label: 'Admin' };
const pounds = (p) => `£${(p / 100).toFixed(2)}`;

// Step 2: how the club pays its monthly Run The Club bill
function BillingSection({ billing, busy, onSetup }) {
  if (!billing) return null;
  const { status, method, bills } = billing;
  const waived = status === 'WAIVED';
  const hasMethod = status === 'ACTIVE' || (status === 'OVERDUE' && method);
  return (
    <div className="bk-card">
      <h2 className="bk-section-title">2. Set up your monthly bill</h2>
      {waived && <p><strong>You have no bill to pay.</strong></p>}
      {!waived && status === 'NOT_SET_UP' && (
        <>
          <p>Choose how to pay your Run The Club bill each month, by Direct Debit or card.</p>
          <button type="button" className="bk-btn bk-btn--primary bk-btn--full" onClick={onSetup} disabled={!!busy}>
            {busy === 'billing' ? 'Opening Stripe…' : 'Set up your monthly bill'}
          </button>
        </>
      )}
      {!waived && status === 'OVERDUE' && <p className="bk-error"><strong>Your last bill hasn't been paid.</strong></p>}
      {!waived && hasMethod && (
        <>
          <p><strong>{method ? `Your bill is paid by ${METHOD_TEXT[method]}` : 'Your bill is set up'}</strong></p>
          <button type="button" className="bk-btn bk-btn--full" onClick={onSetup} disabled={!!busy}>
            {busy === 'billing' ? 'Opening Stripe…' : 'Change how you pay'}
          </button>
        </>
      )}
      {!waived && status === 'OVERDUE' && !hasMethod && (
        <button type="button" className="bk-btn bk-btn--primary bk-btn--full" onClick={onSetup} disabled={!!busy}>
          {busy === 'billing' ? 'Opening Stripe…' : 'Set up your monthly bill'}
        </button>
      )}
      {bills && bills.length > 0 && (
        <table className="bk-table ad-table" style={{ marginTop: '1rem' }}>
          <thead><tr><th>Month</th><th className="num">Members</th><th className="num">Amount</th><th>Status</th><th /></tr></thead>
          <tbody>
            {bills.map(b => {
              const bill = BILL_STATUS[b.status] || { label: b.status, tone: 'off' };
              return (
                <tr key={b.id}>
                  <td className="ad-card-title">{formatMonth(b.month)}</td>
                  <td className="num" data-label="Members">{b.activeMembers}</td>
                  <td className="num" data-label="Amount">{pounds(b.amountPence)}</td>
                  <td className="ad-card-status"><span className={`bk-pill bk-pill--${bill.tone}`}>{bill.label}</span></td>
                  <td>{b.hostedInvoiceUrl && <a className="bk-link" href={b.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer">View invoice</a>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

// Admin, Card payments: the club's own Stripe account, which families pay by card
export default function AdminCardPayments() {
  // Connecting Stripe needs Manage access to payments; the Run The Club
  // bill is for Club admins only
  const { user, updateUser, can, isClubAdmin } = useAuth();
  const managesPayments = can('payments', 'MANAGE');
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [outcome, setOutcome] = useState(null);
  const [settingUp, setSettingUp] = useState(false);
  const started = useRef(false);

  const showStatus = (data) => {
    setStatus(data);
    // Keep the signed-in user's flag current so other screens stop saying card payments are off
    if (user && user.cardPayments !== data.cardPayments) updateUser({ ...user, cardPayments: data.cardPayments });
  };

  const goToStripe = async (which) => {
    setBusy(which); setError(null); setOutcome(null);
    try {
      const res = which === 'existing' ? await bookingApi.getOAuthLink() : await bookingApi.payoutsOnboardingLink();
      window.location.assign(res.data.url);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't reach Stripe. Try again.");
      setBusy(null);
    }
  };

  // The admin closed Stripe's set-up form: read where the account has got to
  const finishedSetup = () => {
    setSettingUp(false);
    bookingApi.refreshPayouts()
      .then(res => showStatus(res.data))
      .catch(err => setError(err.response?.data?.error || "Couldn't load card payments."));
  };

  const setUpBill = async () => {
    setBusy('billing'); setError(null); setOutcome(null);
    try {
      const res = await bookingApi.billingSetup('card-payments');
      window.location.assign(res.data.url);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't reach Stripe. Try again.");
      setBusy(null);
    }
  };

  // Try again after the page couldn't load
  const reload = () => {
    setError(null);
    bookingApi.getPayouts()
      .then(res => showStatus(res.data))
      .catch(err => setError(err.response?.data?.error || "Couldn't load card payments."));
  };

  useEffect(() => {
    // Runs once, even when React StrictMode runs effects twice: the OAuth code
    // can only be used once
    if (started.current) return;
    started.current = true;

    // Back from saving how to pay the bill
    const billingBack = params.get('billing');
    if (billingBack === 'done') setOutcome({ ok: true, text: 'Thanks. Your payment details are saved.' });
    if (billingBack === 'cancelled') setOutcome({ text: "You didn't finish setting up your bill. Nothing has changed." });

    const oauth = params.get('oauth');
    const code = params.get('code');
    const state = params.get('state');
    // Back from setting up a new account: read it now rather than waiting for its webhook.
    // A stale Stripe link sends us back with refresh=1: make a new one.
    const back = params.get('returned') === '1';
    const stale = params.get('refresh') === '1';
    if (oauth || back || stale || billingBack) navigate(pathname, { replace: true });

    const load = () => (back ? bookingApi.refreshPayouts() : bookingApi.getPayouts())
      .then(res => { showStatus(res.data); return true; })
      .catch(err => { setError(err.response?.data?.error || "Couldn't load card payments."); return false; });

    if (oauth === 'complete') {
      bookingApi.completeOAuth(code, state)
        .then(res => setOutcome(OUTCOMES[res.data.outcome] || OUTCOMES.error))
        .catch(err => setOutcome(err.response?.status === 403 && err.response.data?.error
          ? { text: err.response.data.error }
          : OUTCOMES.error))
        .finally(load);
      return;
    }
    if (OUTCOMES[oauth]) setOutcome(OUTCOMES[oauth]);
    load().then(ok => { if (ok && stale) goToStripe('new'); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const message = outcome && (
    <p className={outcome.ok ? undefined : 'bk-error'} role="status"><strong>{outcome.text}</strong></p>
  );

  const header = <PageHeader back={ADMIN} title="Card payments" />;
  if (error && !status) {
    return (
      <div className="bk-page bk-page--narrow">
        {header}
        {message}
        <ErrorState title="Couldn't load card payments" text={error} onRetry={reload} />
      </div>
    );
  }
  if (!status) return <div className="bk-page bk-page--narrow">{header}<Skeleton rows={2} label="Loading card payments…" /></div>;
  if (status.houseClub) {
    return (
      <div className="bk-page bk-page--narrow">
        {header}
        <p>This club takes card payments on Run The Club's own account, so there's nothing to set up.</p>
      </div>
    );
  }

  const s = status.payoutsStatus;
  // A club can connect an account it already uses until its own is up and running
  const canConnect = ['NOT_STARTED', 'PENDING'].includes(s) || !s;
  return (
    <div className="bk-page bk-page--narrow">
      {header}
      {message}
      {error && <p className="bk-error">{error}</p>}

      <div className="bk-card">
        <h2 className="bk-section-title">1. Connect Stripe</h2>
        <p>Families' card payments go straight into your club's own Stripe account.</p>
        {STATUS_TEXT[s] && <p><strong>{STATUS_TEXT[s]}</strong></p>}
        {status.embedded ? (
          <>
            {managesPayments && canConnect && !settingUp && (
              <div className="bk-actions">
                <button type="button" className="bk-btn bk-btn--primary" onClick={() => setSettingUp(true)} disabled={!!busy}>
                  {status.hasAccount ? 'Carry on setting up' : 'Set up card payments'}
                </button>
                <button type="button" className="bk-btn" onClick={() => goToStripe('existing')} disabled={!!busy}>
                  {busy === 'existing' ? 'Opening Stripe…' : 'We already use Stripe'}
                </button>
              </div>
            )}
            {managesPayments && (settingUp || ['ACTIVE', 'RESTRICTED'].includes(s)) && (
              <EmbeddedStripe active={s === 'ACTIVE'} onFinished={finishedSetup} />
            )}
          </>
        ) : (
          <>
            {managesPayments && canConnect && (
              <div className="bk-actions">
                <button type="button" className="bk-btn bk-btn--primary" onClick={() => goToStripe('existing')} disabled={!!busy}>
                  {busy === 'existing' ? 'Opening Stripe…' : 'I already use Stripe'}
                </button>
                <button type="button" className="bk-btn" onClick={() => goToStripe('new')} disabled={!!busy}>
                  {busy === 'new' ? 'Opening Stripe…' : status.hasAccount ? 'Carry on with Stripe' : 'New Stripe account'}
                </button>
              </div>
            )}
            {managesPayments && s === 'RESTRICTED' && (
              <button type="button" className="bk-btn bk-btn--primary bk-btn--full" onClick={() => goToStripe('new')} disabled={!!busy}>
                {busy === 'new' ? 'Opening Stripe…' : 'Update Stripe details'}
              </button>
            )}
          </>
        )}
      </div>

      {isClubAdmin && <BillingSection billing={status.billing} busy={busy} onSetup={setUpBill} />}
    </div>
  );
}
